// 2D collision helpers on the XZ plane. Everything is a circle (cars) against boxes/circles (world).

import type { Obstacle } from './ground';

export interface Hit {
  /** push-out vector to apply to the circle */
  nx: number;
  nz: number;
  depth: number;
}

/** Circle at (x,z) radius r against an oriented box obstacle. */
export function circleVsBox(x: number, z: number, r: number, o: Obstacle): Hit | null {
  const dx = x - o.p[0], dz = z - o.p[2];
  const c = o.cos!, s = o.sin!;
  // into box local space (rotate by -yaw)
  const lx = dx * c + dz * s;
  const lz = -dx * s + dz * c;
  const cx = Math.max(-o.h[0], Math.min(o.h[0], lx));
  const cz = Math.max(-o.h[1], Math.min(o.h[1], lz));
  let ddx = lx - cx, ddz = lz - cz;
  let d = Math.hypot(ddx, ddz);
  if (d >= r) return null;
  if (d < 1e-6) {
    // centre inside the box: push out through the nearest face
    const px = o.h[0] - Math.abs(lx), pz = o.h[1] - Math.abs(lz);
    if (px < pz) { ddx = Math.sign(lx) || 1; ddz = 0; d = -px; }
    else { ddx = 0; ddz = Math.sign(lz) || 1; d = -pz; }
    const depth = r - d;
    return { nx: ddx * c - ddz * s, nz: ddx * s + ddz * c, depth };
  }
  const nxl = ddx / d, nzl = ddz / d;
  return { nx: nxl * c - nzl * s, nz: nxl * s + nzl * c, depth: r - d };
}

export function circleVsCircle(x: number, z: number, r: number, o: Obstacle): Hit | null {
  const dx = x - o.p[0], dz = z - o.p[2];
  const d = Math.hypot(dx, dz);
  const rr = r + o.h[0];
  if (d >= rr) return null;
  if (d < 1e-6) return { nx: 1, nz: 0, depth: rr };
  return { nx: dx / d, nz: dz / d, depth: rr - d };
}

export function circleVsObstacle(x: number, z: number, r: number, o: Obstacle): Hit | null {
  return o.k === 'circle' ? circleVsCircle(x, z, r, o) : circleVsBox(x, z, r, o);
}
